/**
 * Planning Scenarios API Hook
 * Provides data fetching and mutations for AI Planning Scenarios
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';

const API_BASE = '/planning';

// Scenario status enum
export type ScenarioStatus =
  | 'draft'
  | 'calculating'
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'applied'
  | 'failed';

// Planning Scenario from the API
export interface PlanningScenario {
  id: string;
  name: string;
  description: string;
  status: ScenarioStatus;
  planning_start: string;
  planning_end: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;

  // AI-related fields
  ai_model: string | null;
  ai_prompt_tokens: number | null;
  ai_completion_tokens: number | null;
  ai_total_tokens: number | null;
  ai_processing_time: number | null;

  // Metrics
  on_time_rate: number | null;
  capacity_utilization: number | null;
  total_operations: number;
  total_conflicts: number;

  // Related counts (from serializer)
  production_order_count?: number;
  planned_operation_count?: number;
  conflict_count?: number;
}

// Scenario summary for dashboard
export interface ScenarioSummary {
  total: number;
  by_status: Record<ScenarioStatus, number>;
  recent: PlanningScenario[];
}

// Create scenario payload
export interface CreateScenarioPayload {
  name: string;
  description?: string;
  planning_start: string;
  planning_end: string;
  optimization_config?: Record<string, number>;
  human_instructions?: string;
}

// Create a new scenario
async function createScenario(payload: CreateScenarioPayload): Promise<PlanningScenario> {
  const response = await api.post(`${API_BASE}/scenarios/`, payload);
  return response.data;
}

// Delete a scenario
async function deleteScenario(scenarioId: string): Promise<void> {
  await api.delete(`${API_BASE}/scenarios/${scenarioId}/`);
}

// Fetch all scenarios
async function fetchScenarios(): Promise<PlanningScenario[]> {
  const response = await api.get(`${API_BASE}/scenarios/`);
  return response.data.results || response.data;
}

// Fetch scenario summary
async function fetchScenarioSummary(): Promise<ScenarioSummary> {
  const scenarios = await fetchScenarios();

  const byStatus: Record<ScenarioStatus, number> = {
    draft: 0,
    calculating: 0,
    pending_review: 0,
    approved: 0,
    rejected: 0,
    applied: 0,
    failed: 0,
  };

  scenarios.forEach(s => {
    if (byStatus[s.status] !== undefined) {
      byStatus[s.status]++;
    }
  });

  // Sort by updated_at descending
  const sorted = [...scenarios].sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );

  return {
    total: scenarios.length,
    by_status: byStatus,
    recent: sorted.slice(0, 5), // Last 5 scenarios
  };
}

// Trigger AI calculation
async function triggerCalculation(scenarioId: string): Promise<{ job_id: string }> {
  const response = await api.post(`${API_BASE}/scenarios/${scenarioId}/calculate/`, {});
  return response.data;
}

// Approve scenario
async function approveScenario(scenarioId: string, reason?: string): Promise<PlanningScenario> {
  const response = await api.post(`${API_BASE}/scenarios/${scenarioId}/approve/`, { reason });
  return response.data;
}

// Reject scenario
async function rejectScenario(scenarioId: string, reason?: string): Promise<PlanningScenario> {
  const response = await api.post(`${API_BASE}/scenarios/${scenarioId}/reject/`, { reason });
  return response.data;
}

// Apply scenario (write-back)
async function applyScenario(scenarioId: string): Promise<{ job_id: string }> {
  const response = await api.post(`${API_BASE}/scenarios/${scenarioId}/apply/`, {});
  return response.data;
}

// Hook: Fetch all scenarios
export function usePlanningScenarios() {
  return useQuery({
    queryKey: ['planning-scenarios'],
    queryFn: fetchScenarios,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

// Hook: Fetch scenario summary
export function usePlanningScenarioSummary() {
  return useQuery({
    queryKey: ['planning-scenarios-summary'],
    queryFn: fetchScenarioSummary,
    refetchInterval: 30000,
  });
}

// Hook: Trigger calculation mutation
export function useCalculateScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: triggerCalculation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios-summary'] });
      queryClient.invalidateQueries({ queryKey: ['scenario-detail'] });
      queryClient.invalidateQueries({ queryKey: ['scenario-audit-trail'] });
    },
  });
}

// Hook: Approve scenario mutation
export function useApproveScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => approveScenario(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios-summary'] });
      queryClient.invalidateQueries({ queryKey: ['scenario-detail'] });
      queryClient.invalidateQueries({ queryKey: ['scenario-audit-trail'] });
    },
  });
}

// Hook: Reject scenario mutation
export function useRejectScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => rejectScenario(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios-summary'] });
      queryClient.invalidateQueries({ queryKey: ['scenario-detail'] });
      queryClient.invalidateQueries({ queryKey: ['scenario-audit-trail'] });
    },
  });
}

// Hook: Apply scenario mutation
export function useApplyScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: applyScenario,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios-summary'] });
      queryClient.invalidateQueries({ queryKey: ['scenario-detail'] });
      queryClient.invalidateQueries({ queryKey: ['scenario-audit-trail'] });
    },
  });
}

// Hook: Delete a scenario
export function useDeleteScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteScenario,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios-summary'] });
    },
  });
}

// Hook: Create a new scenario
export function useCreateScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createScenario,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['planning-scenarios-summary'] });
    },
  });
}

// Helper: Get status display info
export function getScenarioStatusInfo(status: ScenarioStatus) {
  const statusMap: Record<ScenarioStatus, { label: string; color: string; bgColor: string }> = {
    draft: { label: 'Draft', color: 'text-gray-600', bgColor: 'bg-gray-100' },
    calculating: { label: 'Calculating', color: 'text-blue-600', bgColor: 'bg-blue-100' },
    pending_review: { label: 'Pending Review', color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
    approved: { label: 'Approved', color: 'text-green-600', bgColor: 'bg-green-100' },
    rejected: { label: 'Rejected', color: 'text-red-600', bgColor: 'bg-red-100' },
    applied: { label: 'Applied', color: 'text-purple-600', bgColor: 'bg-purple-100' },
    failed: { label: 'Failed', color: 'text-red-600', bgColor: 'bg-red-100' },
  };

  return statusMap[status] || statusMap.draft;
}

// Audit trail entry
export interface AuditTrailEntry {
  id: string;
  scenario: string;
  action: string;
  action_display: string;
  performed_by: string;
  comment: string;
  details: Record<string, unknown>;
  old_status: string | null;
  new_status: string | null;
  created_at: string;
}

// Fetch single scenario detail
async function fetchScenarioDetail(scenarioId: string): Promise<PlanningScenario> {
  const response = await api.get(`${API_BASE}/scenarios/${scenarioId}/`);
  return response.data;
}

// Fetch audit trail for a scenario
async function fetchAuditTrail(scenarioId: string): Promise<AuditTrailEntry[]> {
  const response = await api.get(`${API_BASE}/scenarios/${scenarioId}/audit-trail/`);
  return response.data.results || response.data;
}

// Hook: Fetch single scenario detail
export function useScenarioDetail(scenarioId: string | undefined) {
  return useQuery({
    queryKey: ['scenario-detail', scenarioId],
    queryFn: () => fetchScenarioDetail(scenarioId!),
    enabled: !!scenarioId,
    refetchInterval: 15000, // Refresh every 15 seconds (faster for detail view)
  });
}

// Hook: Fetch audit trail
export function useAuditTrail(scenarioId: string | undefined) {
  return useQuery({
    queryKey: ['scenario-audit-trail', scenarioId],
    queryFn: () => fetchAuditTrail(scenarioId!),
    enabled: !!scenarioId,
    refetchInterval: 60000, // Refresh every minute
  });
}

// Add a comment to a scenario
async function addScenarioComment(scenarioId: string, comment: string): Promise<AuditTrailEntry> {
  const response = await api.post(`${API_BASE}/scenarios/${scenarioId}/add-comment/`, { comment });
  return response.data;
}

// Hook: Add comment to a scenario
export function useAddComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scenarioId, comment }: { scenarioId: string; comment: string }) =>
      addScenarioComment(scenarioId, comment),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['scenario-audit-trail', variables.scenarioId] });
      queryClient.invalidateQueries({ queryKey: ['scenario-detail', variables.scenarioId] });
    },
  });
}

// Widget data helper
export function usePlanningScenarioWidgetData() {
  const { data, isLoading, refetch } = usePlanningScenarioSummary();
  const calculateMutation = useCalculateScenario();

  return {
    summary: data,
    loading: isLoading,
    onRefresh: refetch,
    onCalculate: calculateMutation.mutateAsync,
    isCalculating: calculateMutation.isPending,
  };
}
